import type { StorageUsageResponse } from "@poseref/shared";
import { create } from "zustand";
import type { AuthUser } from "../api/auth.ts";
import * as authApi from "../api/auth.ts";
import { fetchStorageUsage } from "../api/library.ts";

interface AuthState {
  /** Current user, or null if not authenticated. */
  user: AuthUser | null;
  /** True while checking session or logging in. */
  loading: boolean;
  /** Error message from the last auth attempt. */
  error: string | null;
  /** Current storage usage and quota info. */
  storageUsage: StorageUsageResponse | null;

  /** Check if we have a valid session cookie. Called on app mount. */
  checkSession: () => Promise<void>;
  /** Sign in with email and password. */
  login: (email: string, password: string) => Promise<void>;
  /** Sign up with name, email, and password. */
  register: (name: string, email: string, password: string) => Promise<void>;
  /** Sign out and clear user state. */
  logout: () => Promise<void>;
  /** Called by the API client on 401 — clears user without an API call. */
  handleUnauthorized: () => void;
  /** Refresh storage usage from the API. */
  refreshStorageUsage: () => Promise<void>;
  /** Update the cached user object (e.g. after profile edit). */
  updateUser: (updates: Partial<AuthUser>) => void;
}

const DEV_USER: AuthUser = {
  id: "dev-user",
  name: "Dev User",
  email: "dev@poseref.local",
  image: null,
};

function isDevAuthBypassEnabled(): boolean {
  return import.meta.env.DEV;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  storageUsage: null,

  checkSession: async () => {
    set({ loading: true, error: null });
    if (isDevAuthBypassEnabled()) {
      set({ user: DEV_USER, loading: false });
      await get().refreshStorageUsage();
      return;
    }
    const user = await authApi.getSession();
    set({ user, loading: false });
    if (user) {
      await get().refreshStorageUsage();
    }
  },

  login: async (email, password) => {
    if (isDevAuthBypassEnabled()) {
      set({ user: DEV_USER, loading: false, error: null });
      await get().refreshStorageUsage();
      return;
    }
    set({ loading: true, error: null });
    try {
      const user = await authApi.signIn(email, password);
      set({ user, loading: false });
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Login failed",
      });
      throw e;
    }
  },

  register: async (name, email, password) => {
    if (isDevAuthBypassEnabled()) {
      set({ user: DEV_USER, loading: false, error: null });
      await get().refreshStorageUsage();
      return;
    }
    set({ loading: true, error: null });
    try {
      const user = await authApi.signUp(name, email, password);
      set({ user, loading: false });
    } catch (e) {
      set({
        loading: false,
        error: e instanceof Error ? e.message : "Registration failed",
      });
      throw e;
    }
  },

  logout: async () => {
    if (isDevAuthBypassEnabled()) {
      set({ user: DEV_USER, loading: false, error: null });
      await get().refreshStorageUsage();
      return;
    }
    try {
      await authApi.signOut();
    } catch {
      // Ignore — clear local state regardless
    }
    set({ user: null, loading: false, error: null, storageUsage: null });
  },

  handleUnauthorized: () => {
    if (isDevAuthBypassEnabled()) {
      set({ user: DEV_USER, loading: false, error: null });
      return;
    }
    set({ user: null, loading: false, error: null, storageUsage: null });
  },

  refreshStorageUsage: async () => {
    try {
      const usage = await fetchStorageUsage();
      set({ storageUsage: usage });
    } catch {
      // Non-critical — don't block on storage usage fetch failures
    }
  },

  updateUser: (updates) => {
    const current = get().user;
    if (current) set({ user: { ...current, ...updates } });
  },
}));

/** Returns true when the authenticated user is on the free trial (unsubscribed tier). */
export function useIsTrial(): boolean {
  return useAuthStore((s) => s.storageUsage?.tier === "unsubscribed");
}
